import React, { useCallback } from 'react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Download, Check, Loader2, Smartphone } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { usePWAInstall } from '@/hooks/usePWAInstall';
import AddToHomeScreen from '@/components/AddToHomeScreen';

interface InstallAppStepProps {
  onContinue: () => void;
}

const InstallAppStep: React.FC<InstallAppStepProps> = ({ onContinue }) => {
  const { t } = useTranslation();
  const { 
    isInstallable, 
    isInstalled, 
    isInstalling, 
    platform,
    installApp,
    installOutcome
  } = usePWAInstall();

  const handleInstallClick = useCallback(async () => {
    await installApp();
    // After install, we show success state with "open app" message
  }, [installApp]);

  // Success state - app is already installed or user just accepted
  // Show message to close browser and open the app
  if (isInstalled || installOutcome === 'accepted') {
    return (
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="text-center text-green-700">
            {t('install.alreadyInstalled')}
          </CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col items-center space-y-4">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center">
            <Check className="w-10 h-10 text-green-600" />
          </div>
          <p className="text-center text-green-600">
            {t('install.successMessage')}
          </p>
          <p className="text-sm text-muted-foreground text-center font-medium">
            {t('install.openAppToContinue')}
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="w-full max-w-md">
      <CardHeader>
        <CardTitle className="text-center">{t('install.title')}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col items-center space-y-4">
        <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center">
          {platform === 'ios' ? (
            <Smartphone className="w-8 h-8 text-teal-600" />
          ) : (
            <Download className="w-8 h-8 text-teal-600" />
          )}
        </div>
        <p className="text-center text-muted-foreground">
          {t('install.description')}
        </p>
        
        {/* Native install button for supported browsers */}
        {isInstallable && (
          <div className="w-full pt-2">
            <Button 
              onClick={handleInstallClick}
              disabled={isInstalling}
              className="w-full h-14 text-lg bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700"
            >
              {isInstalling ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  {t('install.installing')}
                </>
              ) : (
                <>
                  <Download className="w-5 h-5 mr-2" />
                  {t('install.button')}
                </>
              )}
            </Button>
          </div>
        )}
        
        {/* Manual instructions for iOS or fallback */}
        {(platform === 'ios' || !isInstallable) && (
          <div className="w-full">
            <AddToHomeScreen embedded={true} onInstalled={onContinue} />
          </div>
        )}
        
        {/* Confirmation button for manual installation */}
        {!isInstallable && (
          <div className="w-full pt-4">
            <Button 
              onClick={onContinue} 
              variant="outline"
              className="w-full h-12"
            >
              <Check className="w-4 h-4 mr-2" />
              {t('install.installed')}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default InstallAppStep;
